import AiSuggestions from '@/components/AiSuggestions.jsx';
import Link from '@/components/Link';
import PlatformCurrentPlan from '@/components/platform/PlatformCurrentPlan.jsx';
import usePrimaryNavScrollCollapse from '@/components/platform/platformPrimaryNav/usePrimaryNavScrollCollapse.js';
import { FiBook, FiChevronDown, FiLock, FiLogOut, FiUser, MdOutlineArrowOutward, MdOutlineRocketLaunch, PiSlackLogo } from '@/components/react-icons/main';
import TextWithEllipsis from '@/components/TextWithEllipsis.jsx';
import FloatingSelectV2 from '@/components/v2/FloatingSelectV2.jsx';
import Stack from '@/components/v3/Stack';
import useLogoutAndRedirect from '@/hooks/useLogoutAndRedirect.js';
import usePlatformBreadcrumb from '@/hooks/usePlatformBreadcrumb.js';
import useUser from '@/hooks/useUser.js';
import setPath from '@/lib/setPath.js';
import { setModalState } from '@/redux/actions/uiActions/modalActions.js';
import routes from '@/routes.js';
import { useSelector } from 'react-redux';
import PresenceHandler from '../PresenceHandler.jsx';
import IconV2 from '../v2/IconV2.jsx';
import PlatformCompanySelect from './PlatformCompanySelect.jsx';
import PlatformMobileNav from './PlatformMobileNav.jsx';
import SyncStateLoader from './SyncStateLoader.jsx';
import { buildWebsiteUrl } from '@shared/utils/websiteBaseUrl.js';

const PlatformPrimaryNav = () => {
  const { jwt } = useUser();
  const logoutAndRedirect = useLogoutAndRedirect();
  const breadcrumb = usePlatformBreadcrumb();
  const activeRouteId = useSelector((state) => state.ui.activeRouteId);
  const activeRoute = routes.find((n) => n.id === activeRouteId);
  const isCollapsed = usePrimaryNavScrollCollapse();

  const handleOptionClick = (option) => {
    if (option?.value === 'logout') {
      logoutAndRedirect();
    } else if (option?.value === 'profile') {
      setModalState('permissions', { open: true });
    } else if (option?.value === 'userProfile') {
      setModalState('userProfile', { open: true });
    }
  };

  const handleBreadcrumbClick = (item) => {
    if (item?.route) {
      setPath(item.route, { replace: true });
    }
  };

  const userDropdownOptions = [
    { icon: <FiUser />, label: 'Profile', value: 'userProfile' },
    { icon: <FiLock />, label: 'Permissions', value: 'profile' },
    { icon: <FiLogOut />, label: 'Log Out', value: 'logout' }
  ];

  return (
    <Stack justify={'between'} className={`mc-primary-nav route-${activeRoute?.routeId} ${isCollapsed ? 'collapsed' : ''}`}>
      <Stack gap={'xs'}>
        <Link to={'/'}>
          <Stack className={'logo-wrapper'} data-title={'Home'}>
            <img className={'logo'} src={'/images/logo-mark.svg'} alt={'Logo'} />
          </Stack>
        </Link>
        <PlatformMobileNav />
        <Stack className={'mobile-hidden'} gap={'xs'} style={{ marginTop: '2px' }}>
          <PlatformCompanySelect active={breadcrumb?.length === 0} />
          {breadcrumb.map((item, index) => (
            <Stack key={`${item.value}-${index}`} className={`mc-breadcrumb-item-${index} ${item?.route && item?.items ? 'clickable' : ''} ${item?.type === 'divider' ? 'divider' : ''}`}>
              {item?.image && <img src={item.image} alt={item.value} />}
              {item?.route && (
                <Link to={item?.route}>
                  <div className={`${item?.type === 'divider' ? 'nav-divider' : 'nav-item'} has-route`} data-title={item?.dataTitle}>
                    <TextWithEllipsis>{item.value}</TextWithEllipsis>
                  </div>
                </Link>
              )}
              {!item?.route && (
                <div className={`${item?.type === 'divider' ? 'nav-divider' : 'nav-item'}`} data-title={item?.dataTitle}>
                  <TextWithEllipsis>{item.value}</TextWithEllipsis>
                </div>
              )}
              {item?.items && item?.items?.length > 0 && (
                <>
                  <div role={'button'} className={`mc-breadcrumb-select-${index} ${item?.route ? 'has-route' : ''}`} data-title={item?.route ? 'Switch sections' : `Switch ${activeRoute?.routeId}`}>
                    <IconV2 icon={item.icon || FiChevronDown} />
                  </div>
                  <FloatingSelectV2 triggers={item?.route ? [`.mc-breadcrumb-select-${index}`] : [`.mc-breadcrumb-item-${index}`]} onChange={handleBreadcrumbClick} options={item.items} offset={8} useSearch={true} />
                </>
              )}
            </Stack>
          ))}
        </Stack>
      </Stack>
      <Stack gap={'sm'}>
        <SyncStateLoader />
        <AiSuggestions />
        <PresenceHandler />
        <Link className={'mobile-hidden'} to={'https://join.slack.com/t/mindcloudusers/shared_invite/zt-2s88hy60h-MFNJeWSzYtrlMFUl8MN1zg'} target={'_blank'}>
          <div className={'nav-external-link'}>
            <IconV2 icon={PiSlackLogo} />
            <TextWithEllipsis>Slack Community</TextWithEllipsis>
          </div>
        </Link>
        <Link className={'mobile-hidden'} to={buildWebsiteUrl('/changelog')} target={'_blank'}>
          <div className={'nav-external-link'}>
            <IconV2 icon={MdOutlineRocketLaunch} />
            <TextWithEllipsis>Changelog</TextWithEllipsis>
          </div>
        </Link>
        <Link className={'mobile-hidden'} to={'https://mindcloud.co/docs'} target={'_blank'}>
          <div className={'nav-external-link docs'}>
            <IconV2 icon={FiBook} />
            Docs
            <IconV2 icon={MdOutlineArrowOutward} size={8} />
          </div>
        </Link>
        <PlatformCurrentPlan />
        <div className={'avatar-wrapper'} data-title={jwt?.name}>
          <img src={jwt?.imageUrl} alt={jwt?.name} />
        </div>
        <FloatingSelectV2 triggers={['.avatar-wrapper']} options={userDropdownOptions} onChange={handleOptionClick} offset={8} useSearch={false} />
      </Stack>
    </Stack>
  );
};

export default PlatformPrimaryNav;
